import client from './httpClient';
import { formDataToCreateBookingPayload, locationParamsToPayload } from './transformer';
import { CITIES, UNIVERSITIES, NEIGHBOURHOODS } from '@spa/shared/constants/locationTypes';
import { cityCodeToCitySlug } from '@spa/shared/utils/cityCodeToCitySlug';
import { transformUniversityData, transformNeighborhoodData, transformDynamicData } from './transformer';
import { handleDatadogError } from '@spa/shared/utils/datadog';

export const addAuthentication = token => {
  client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const deleteAuthentication = () => {
  delete client.defaults.headers.common['Authorization'];
};

export const fetchGuest = id => client.get(`guests/${id}`).then(({ data }) => data);

export const signup = payload =>
  client
    .post('guests', payload, {
      headers: {}
    })
    .then(({ data }) => data);

export const emailExists = email => client.get(`guests/${email}/exists-email-address`).then(({ data }) => data);

export const signupSocial = payload => client.post('guests-social', payload).then(({ data }) => data);

export const initiateAccountRecovery = payload =>
  client.put('accounts/recovery/initiate', payload).then(({ data }) => data);

export const accountRecovery = payload => client.put('accounts/recovery', payload).then(({ data }) => data);

export const getLocation = async params => {
  const { type, city, name } = locationParamsToPayload(params);

  const location = await getLocationByType(type, name, city);
  if (!location) {
    throw new Error(`Could not get location: ${name}.`);
  }

  const locationWithCity = await appendCityToLocation({ ...location, type });

  return Promise.resolve({ ...locationWithCity, type });
};

const getLocationByType = async (type, name, city) => {
  if (![CITIES, UNIVERSITIES, NEIGHBOURHOODS].includes(type)) {
    throw new Error(
      `Unexpected location type: ${type}. Expected values are: ${CITIES}, ${NEIGHBOURHOODS}, ${UNIVERSITIES}.`
    );
  }

  if (type === CITIES) {
    return await getCity(name);
  }

  const cityFixed = await handleCityCodeOrSlug(city);

  return await (type === NEIGHBOURHOODS ? getNeighbourhood(cityFixed, name) : getUniversity(cityFixed, name));
};

const handleCityCodeOrSlug = async city => {
  if (city.indexOf('-') > -1) {
    return Promise.resolve(city);
  }

  const { code } = await (city.indexOf('-') > -1 ? Promise.resolve({ code: city }) : getCity(city));

  return code;
};

export const getCity = slug => {
  if (!slug) {
    // eslint-disable-next-line no-console
    console.trace(slug); //temporary log to understand where the value comes

    return Promise.resolve(null);
  }

  return client.get(`cities/slug/${slug}`).then(({ data }) => data);
};

export const getUniversity = (cityCode, slug) => client.get(`colleges/${cityCode}/${slug}`).then(({ data }) => data);

export const getNeighbourhood = (cityCode, slug) =>
  client.get(`neighborhoods/${cityCode}/${slug}`).then(({ data }) => data);

const appendCityToLocation = async location => {
  if (!location) {
    return null;
  }

  if (location.type === CITIES) {
    return location;
  }

  const {
    configuration: { city_code: cityCode }
  } = location;

  const city = await getCity(cityCodeToCitySlug(cityCode));

  return { ...location, city };
};

export const replaceGuestWishlist = (guestId, wishlist) =>
  client.put(`guests/${guestId}/wish-list`, { guest_replace_wish_list: wishlist });

export const getUniversityData = (city, uid) =>
  client.get(`college-pages-aggregate/${city}/${uid}`).then(transformUniversityData);

export const getNeighborhoodData = (city, uid) =>
  client.get(`neighborhood-pages-aggregate/${city}/${uid}`).then(transformNeighborhoodData);

export const getDynamicPageData = uid => client.get(`dynamic-pages/${uid}`).then(transformDynamicData);

export const pricingRequest = data =>
  client
    .post('pricing-requests', { pricing_request_create: data })
    .then(({ data }) => data)
    .catch(error => handleDatadogError('Error calculating pricing request', data, error));

export const neighborhoodPageAggregate = (city, neighborhood) =>
  client
    .get(`neighborhood-pages-aggregate/${city}/${neighborhood}?citySlug=${city}&neighborhoodSlug=${neighborhood}`)
    .then(({ data }) => data);

export const createBooking = formData =>
  client
    .post('/bookings', { booking_new_create: formDataToCreateBookingPayload(formData) })
    .then(({ data: booking, headers }) => ({ booking, headers }))
    .catch(error => handleDatadogError('Error creating booking', formDataToCreateBookingPayload(formData), error));

export const replaceBookingGuestAboutInfo = (bookingId, payload) =>
  client
    .put(`bookings/${bookingId}/guest-about-info`, { booking_guest_about_info: payload })
    .catch(error => handleDatadogError('Error replacing guest info', payload, error));

export const getInvoiceDetails = guestId => client.get(`/invoice-details/guest/${guestId}`).then(({ data }) => data);

export const getInvoiceDetailsUnauthenticated = (guestId, secret) =>
  client.get(`/invoice-details/guest/${guestId}/secret/${secret}`).then(({ data }) => data);

export const getGuestSecretUnauthenticated = (bookingId, bookingSecret) =>
  client.get(`/guests/secret/${bookingId}/${bookingSecret}`).then(({ data }) => data);

export const getGuestUnauthenticated = (guestId, guestSecret) =>
  client.get(`/guests/${guestId}/${guestSecret}/unauthenticated`).then(({ data }) => data);

export const bookingSecretByBookingAndGuestId = (bookingId, guestId) =>
  client.get(`/bookings/guest/${bookingId}/${guestId}`).then(({ data }) => data);

export const createInvoiceDetailsUnauthenticated = (secret, data) =>
  client.post(`/invoice-details/guest/secret/${secret}`, data).then(({ data }) => data);

export const getBookingById = bookingId => client.get(`/bookings/${bookingId}`).then(({ data }) => data);

export const getBookingByIdUnauthenticated = (bookingId, secret) =>
  client.get(`/bookings/${bookingId}/${secret}`).then(({ data }) => data);

export const replaceGuestPassport = (id, secret = '', passport) =>
  (secret
    ? client.put(`guests/${id}/passport/${secret}`, { guest_replace_passport: { passport } })
    : client.put(`guests/${id}/passport`, { guest_replace_passport: { passport } })
  ).catch(error => handleDatadogError('Error replacing guest passport', passport, error));

export const replaceGuestDocuments = (id, documents) =>
  client
    .put(`guests/${id}/documents`, { guest_replace_documents: documents })
    .catch(error => handleDatadogError('Error replacing guest documents', documents, error));

export const replaceGuestProfile = (id, profile) =>
  client.put(`guests/${id}/profile`, { guest_replace_profile: profile });

export const replaceGuestNotification = (id, notification) =>
  client.put(`guests/${id}/notification`, { guest_replace_notification: notification });

export const guestDocumentS3PreSignedUrlUpload = (id, fileType) =>
  client.get(`guests/${id}/documents/s3-presigned-url/upload/${fileType}`).then(({ data }) => data);

export const guestDocumentS3PreSignedUrlForDownload = (id, payload) =>
  client
    .post(`guests/${id}/documents/s3-presigned-url/download`, {
      guest_generate_pre_signed_url_for_documents_download: { ...payload }
    })
    .then(({ data }) => data)
    .catch(error =>
      handleDatadogError(
        'Error fetching S3-pre-signed-urls to download guest documents',
        { guestId: id, ...payload },
        error
      )
    );

export const addPaymentToBooking = (bookingId, payload) =>
  client
    .put(`/bookings/${bookingId}/payment-details`, payload)
    .then(({ data }) => data)
    .catch(error => handleDatadogError('Error adding Payment info to booking', { bookingId, ...payload }, error));

export const replaceGuestPaymentInfo = (id, secret, payload) =>
  client
    .put(secret ? `guests/${id}/payment_info/${secret}` : `guests/${id}/payment_info`, payload)
    .then(({ data }) => data)
    .catch(error => handleDatadogError('Error replacing guest payment', payload, error));

export const payBooking = (id, payload, secret = null) =>
  secret
    ? client.put(`/bookings/${id}/pay/${secret}`, payload).then(({ data }) => data)
    : client.put(`/bookings/${id}/pay`, payload).then(({ data }) => data);

export const getAccommodationProviderSecretUnauthenticated = (bookingId, bookingSecret) =>
  client.get(`/bookings/accommodation-provider/secret/${bookingId}/${bookingSecret}`).then(({ data }) => data);

export const getAccommodationProviderUnauthenticated = (id, secret) =>
  client.get(`/accommodation-providers/${id}/${secret}`).then(({ data }) => data);

export const applyPromocode = (id, promocode, secret = null) =>
  secret
    ? client.put(`/bookings/${id}/apply-promocode/${promocode}/${secret}`).then(({ data }) => data)
    : client.put(`/bookings/${id}/apply-promocode/${promocode}`).then(({ data }) => data);

export const requestUserVerificationCode = (username, type) =>
  client.get(`/account/user/${username}/${type}/code`).then(({ data }) => data);

export const authenticateUserByCode = (username, code) =>
  client.post(`/account/user/${username}/authenticate/code/${code}`).then(({ data }) => data);

export const getGuestPaidBookingRequests = guestId => {
  // eslint-disable-next-line prettier/prettier
  const bookingCurrentStatePaid = 'contains([\'paid\'], states.items[-1].code) == `true`';
  const bookingFilterExpression = `items[?${bookingCurrentStatePaid}].{id: id, offerId: offer.id, moveInOut: guest.move_in_out}`;

  return client.get(`/bookings?guest_id=${guestId}&fields=${bookingFilterExpression}`).then(({ data }) => data ?? []);
};

export const createFailedWalletPaymentOperation = (bookingId, payload) =>
  client.put(`/payment-operations/bookings/${bookingId}`, payload).then(({ data }) => data);
